import { Loader } from "lucide-react"
import { lazy, Suspense, type MutableRefObject } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams, useSearchParams } from "react-router"

import { PMB_V2_SECTION_TITLES } from "~/containers/os-agents/components/pre-meeting-brief-v2/section-registry"
import useChampionsTrackingStore from "~/store/champions-tracking"
import useOsAgentsIdStore from "~/store/os.agents_.$id"

const MessageTemplateModal = lazy(
  () => import("../champions-tracking/email-template-modal")
)

const AgentMessageTemplate = lazy(
  () => import("./components/agent-message-template")
)
const AgentAccountPriorities = lazy(
  () => import("./components/agent-account-priorities")
)
const JobsTableData = lazy(() => import("./components/agent-jobs-list"))
const AgentPersonBrief = lazy(() => import("./components/agent-person-brief"))
const PreMeetingBriefV2Widget = lazy(
  () => import("./components/pre-meeting-brief-v2")
)
const AgentKeyExecutives = lazy(
  () => import("~/containers/os-agents/components/agent-key-executives")
)
const AgentNews = lazy(() => import("./components/agent-news"))
const AgentAccountOverview = lazy(
  () => import("./components/agent-account-overview")
)
const AgentOrgChart = lazy(() => import("./components/agent-org-chart"))
const AgentPptOutlinePanel = lazy(() => import("./agent-ppt-outline-panel"))
const PitchDeckPptxGenerator = lazy(() =>
  import("~/containers/pitch-deck/pitch-deck-pptx-generator").then((m) => ({
    default: m.PitchDeckPptxGenerator
  }))
)
const PitchDeckV2PptxGenerator = lazy(() =>
  import("~/containers/pitch-deck-v2").then((m) => ({
    default: m.PitchDeckV2PptxGenerator
  }))
)
// Account-plan deck preview (ENG-2274). Account-scoped sibling of
// PitchDeckV2PptxGenerator — triggered when the account-plan agent emits
// ui_component="AccountPlanBuilderV2".
const AccountPlanPptxGenerator = lazy(() =>
  import("~/containers/agent-os/account-plan/account-plan-pptx-generator").then(
    (m) => ({
      default: m.AccountPlanPptxGenerator
    })
  )
)
// V2 generic-text PPT builder — Opus pipeline. Distinct from
// PresentationOutlineBuilder (V1) and SalesPitchDeckBuilderV2 (opp-coupled).
// Triggered when py emits ui_component="PptBuilderV2" with text_body /
// topic_label / slide_count in resourceParams.
const AgentPptOpusPanel = lazy(() =>
  import("./agent-ppt-opus-panel").then((m) => ({
    default: m.AgentPptOpusPanel
  }))
)
// Document artifact side panel — opens when the v2
// documentGeneratorAgent stamps ui_component="DocumentArtifact".
// Renders PDF iframe / XLSX tables / DOCX HTML + download button.
// htmlPageGeneratorAgent stamps ui_component="HtmlPageBuilder". The panel owns
// generation: the agent hands over the brief and ends its turn.
const AgentHtmlPage = lazy(() => import("./components/agent-html-page"))
const AgentDocumentArtifact = lazy(
  () => import("./components/agent-document-artifact")
)

export default function AgentRightPanel({
  sendMessageRef,
  chatIdOverride,
  chatSessionIdOverride,
  accountName,
  opportunityName
}: {
  sendMessageRef: MutableRefObject<any>
  chatIdOverride?: string
  // Overrides PptBuilderV2's chat-session id (used to write the finished
  // deck's ppt_session_id back to chat history for cross-device resume) for
  // callers where resourceParams.chat_session_id isn't reliably populated —
  // e.g. workbenches mounted outside their agent's native route.
  chatSessionIdOverride?: string
  // Fallback names from the workbench (agent resourceParams often omit them,
  // which left the download filename as a placeholder). (ENG-2741)
  accountName?: string
  opportunityName?: string
}) {
  const [searchParams] = useSearchParams()
  const { id: _rawId } = useParams<{ id: string }>()
  // On the OS agents route (/os/agents/:id), :id is a UUID session ID.
  // When AgentRightPanel is mounted in the workbench portal (priority route),
  // useParams returns the account id (e.g. "10658") — a numeric string that must
  // not be used as a chat session key. chatIdOverride lets the workbench pass the
  // correct ADK session UUID so the stream manager is used and SSE survives unmount.
  const chatId =
    chatIdOverride ?? (_rawId && !/^\d+$/.test(_rawId) ? _rawId : undefined)
  const showSplitter = useOsAgentsIdStore((store) => store.showSplitter)
  const setShowSplitter = useOsAgentsIdStore((store) => store.setShowSplitter)
  // py-processing's executor_agent aliases "GeneratePreMeetingBriefAgentV2"
  // → "GeneratePersonPreMeetingBriefAgent" on the wire (see
  // _UI_COMPONENT_FE_ALIAS). The v2 payload shape always carries `brief_id`
  // at resourceParams.brief_id, the v1 shape never does — use that as the
  // discriminator so a card that arrives under the legacy name still routes
  // to the v2 widget.
  const hasV2BriefShape = Boolean(showSplitter?.resourceParams?.brief_id)
  const isPMBV2 =
    showSplitter?.resourceName === "GeneratePreMeetingBriefAgentV2" ||
    (showSplitter?.resourceName === "GeneratePersonPreMeetingBriefAgent" &&
      hasV2BriefShape)
  const isPMB =
    showSplitter?.resourceName === "GeneratePersonPreMeetingBriefAgent" &&
    !hasV2BriefShape
  const isHtml = showSplitter?.resourceName === "html"
  const isKeyExecutives =
    showSplitter?.resourceName === "GenerateAccountKeyExecsAgent"
  const isJobs = showSplitter?.resourceName === "GenerateAccountJobsAgent"
  const isNews = showSplitter?.resourceName === "GenerateAccountNewsAgent"
  const isRecap =
    showSplitter?.resourceName === "GenerateAccountChangesInsightsRecapAgent"
  const isAccountPriorities =
    showSplitter?.resourceName === "GenerateAccountPrioritiesAgent"
  const isMessageTemplate =
    showSplitter?.resourceName === "GenerateMessageTemplateAgent"
  const isOrgChart =
    showSplitter?.resourceName === "GenerateAccountOrgChartAgent"
  const isPpt =
    showSplitter?.resourceName === "GeneratePresentationBuilderAgent" ||
    showSplitter?.resourceName === "PresentationOutlineBuilder"
  const isPitchDeck = showSplitter?.resourceName === "SalesPitchDeckBuilder"
  const isPitchDeckV2 = showSplitter?.resourceName === "SalesPitchDeckBuilderV2"
  const isAccountPlanV2 = showSplitter?.resourceName === "AccountPlanBuilderV2"
  // V2 generic-text PPT builder (Opus pipeline). Distinct from V1's
  // PresentationOutlineBuilder so the V1 component / outline-streaming
  // path stays untouched.
  const isPptBuilderV2 = showSplitter?.resourceName === "PptBuilderV2"
  // Synthetic placeholder used by the dedicated proactive-proposal
  // route. Forces the split layout open on mount so the chat lives on
  // the left while the right side shows a "generating outline" skeleton
  // until the agent emits its real ui_component frame.
  const isProactiveProposalLoading =
    showSplitter?.resourceName === "ProactiveProposalLoading"
  // Terminal counterpart to the skeleton above (ENG-3542): no turns, and
  // none coming — say so instead of spinning.
  const isProactiveProposalUnavailable =
    showSplitter?.resourceName === "ProactiveProposalUnavailable"
  const isDocumentArtifact = showSplitter?.resourceName === "DocumentArtifact"
  const isHtmlPage = showSplitter?.resourceName === "HtmlPageBuilder"

  return (
    <div className="h-full flex flex-col">
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-full">
            <Loader
              className="text-primary dark:text-primary-light animate-spin"
              size={24}
            />
          </div>
        }
      >
        {isPMB && (
          <AgentPersonBrief
            people={showSplitter?.resourceParams?.people_brief?.data?.[0]}
            account={
              showSplitter?.resourceParams?.account_brief?.data?.results?.[0]
            }
            persona={null}
          />
        )}

        {isPMBV2 && (
          <PreMeetingBriefV2Widget
            params={showSplitter?.resourceParams}
            onClose={() => setShowSplitter(null)}
            onRegenerateSection={(briefId, sectionId) => {
              // Regenerate dispatch — sendMessageRef points at agent-
              // search-v2's onSubmit. The user message + assistant turn
              // both render normally in chat (no suppression), so the
              // turn replays cleanly from history later. The widget's
              // per-section regenerating overlay is the in-flight signal.
              //
              // Query text is a natural-language sentence — the planner
              // uses pmbContext.section_id + mode="regenerate" for the
              // actionable signal, but the text is what the user sees in
              // the chat bubble + history replay.
              const submitFn = sendMessageRef?.current
              if (typeof submitFn === "function") {
                const title =
                  PMB_V2_SECTION_TITLES[sectionId] ??
                  sectionId.replace(/_/g, " ")
                submitFn({
                  agentQuery: `Regenerate the ${title} section`,
                  transferToAgent: "GeneratePreMeetingBriefAgentV2",
                  pmbBriefId: briefId,
                  pmbContext: {
                    brief_id: briefId,
                    section_id: sectionId,
                    mode: "regenerate"
                  }
                })
              }
            }}
          />
        )}

        {isKeyExecutives && (
          <AgentKeyExecutives
            people={showSplitter?.resourceParams?.data?.people}
          />
        )}

        {isJobs && (
          <JobsTableData
            data={showSplitter?.resourceParams?.data}
            isLoading={false}
            totalRecords={showSplitter?.resourceParams?.data?.length || 0}
          />
        )}

        {isNews && <AgentNews events={showSplitter?.resourceParams?.data} />}
        {isRecap && (
          <AgentAccountOverview params={showSplitter?.resourceParams} />
        )}
        {isAccountPriorities && (
          <AgentAccountPriorities data={showSplitter?.resourceParams?.data} />
        )}
        {isMessageTemplate && (
          <AgentMessageTemplate data={showSplitter?.resourceParams?.data} />
        )}
        {isOrgChart && (
          <AgentOrgChart
            people={showSplitter?.resourceParams?.data?.people}
            accountId={searchParams.get("account") || ""}
          />
        )}
        {isPpt && (
          <AgentPptOutlinePanel
            params={showSplitter?.resourceParams}
            onClose={() => setShowSplitter(null)}
            sendMessageRef={sendMessageRef}
          />
        )}
        {/* BEGIN_PITCH_DECK_FEATURE */}
        {isPitchDeck && showSplitter?.resourceParams?.opportunity_id && (
          <PitchDeckPptxGenerator
            key={showSplitter.resourceParams.session_id}
            session_id={showSplitter.resourceParams.session_id}
            opportunity_id={Number(showSplitter.resourceParams.opportunity_id)}
            account_id={Number(showSplitter.resourceParams.account_id)}
            // @ts-expect-error prop type mismatch from resourceParams
            account_name={showSplitter.resourceParams.account_name}
            opportunity_name={showSplitter.resourceParams.opportunity_name}
          />
        )}
        {isPitchDeckV2 &&
          showSplitter?.resourceParams?.mode === "generate" &&
          showSplitter?.resourceParams?.opportunity_id && (
            <PitchDeckV2PptxGenerator
              key={`v2-gen-${chatId ?? showSplitter.resourceParams.opportunity_id}`}
              mode="generate"
              chatId={chatId}
              opportunityId={Number(showSplitter.resourceParams.opportunity_id)}
              accountId={Number(showSplitter.resourceParams.account_id)}
              accountName={
                showSplitter.resourceParams.account_name || accountName
              }
              opportunityName={
                showSplitter.resourceParams.opportunity_name || opportunityName
              }
              templateId={showSplitter.resourceParams.template_id ?? null}
            />
          )}
        {isPitchDeckV2 &&
          showSplitter?.resourceParams?.mode === "iterate" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <PitchDeckV2PptxGenerator
              // ppt_session_id is constant across edits, so _iterationToken
              // is what forces a remount per iterate (ENG-2502/2503).
              key={`v2-iter-${showSplitter.resourceParams.ppt_session_id}-${
                showSplitter.resourceParams._iterationToken ?? ""
              }`}
              mode="iterate"
              chatId={chatId}
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              accountName={
                showSplitter.resourceParams.account_name || accountName
              }
              opportunityName={
                showSplitter.resourceParams.opportunity_name || opportunityName
              }
            />
          )}
        {isPitchDeckV2 &&
          showSplitter?.resourceParams?.mode === "resume" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <PitchDeckV2PptxGenerator
              key={`v2-resume-${showSplitter.resourceParams.ppt_session_id}-${
                showSplitter.resourceParams._iterationToken ?? ""
              }`}
              mode="resume"
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              accountName={
                showSplitter.resourceParams.account_name || accountName
              }
              opportunityName={
                showSplitter.resourceParams.opportunity_name || opportunityName
              }
            />
          )}
        {/* END_PITCH_DECK_FEATURE */}
        {/* Account-plan deck (ENG-2274). Account-scoped: generate needs
            account_id; iterate / resume need ppt_session_id. */}
        {isAccountPlanV2 &&
          showSplitter?.resourceParams?.mode === "generate" &&
          showSplitter?.resourceParams?.account_id && (
            <AccountPlanPptxGenerator
              key={`ap-gen-${showSplitter.resourceParams.account_id}`}
              mode="generate"
              accountId={Number(showSplitter.resourceParams.account_id)}
              accountName={showSplitter.resourceParams.account_name}
            />
          )}
        {isAccountPlanV2 &&
          showSplitter?.resourceParams?.mode === "iterate" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <AccountPlanPptxGenerator
              key={`ap-iter-${showSplitter.resourceParams.ppt_session_id}`}
              mode="iterate"
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              accountName={showSplitter.resourceParams.account_name}
            />
          )}
        {isAccountPlanV2 &&
          showSplitter?.resourceParams?.mode === "resume" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <AccountPlanPptxGenerator
              key={`ap-resume-${showSplitter.resourceParams.ppt_session_id}`}
              mode="resume"
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              accountName={showSplitter.resourceParams.account_name}
            />
          )}
        {/* V2 generic-text PPT builder — Opus pipeline. Three sub-modes
            mirror the salesPitchV2 component: generate (initial run),
            iterate (modifying an existing deck), resume (re-open a
            completed deck). Generate needs text_body+topic_label;
            iterate / resume need ppt_session_id. */}
        {isPptBuilderV2 &&
          showSplitter?.resourceParams?.mode === "iterate" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <AgentPptOpusPanel
              key={`v2-opus-iter-${showSplitter.resourceParams.ppt_session_id}`}
              mode="iterate"
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              topicLabel={showSplitter.resourceParams.topic_label}
            />
          )}
        {isPptBuilderV2 &&
          showSplitter?.resourceParams?.mode === "resume" &&
          showSplitter?.resourceParams?.ppt_session_id && (
            <AgentPptOpusPanel
              key={`v2-opus-resume-${showSplitter.resourceParams.ppt_session_id}`}
              mode="resume"
              pptSessionId={showSplitter.resourceParams.ppt_session_id}
              topicLabel={showSplitter.resourceParams.topic_label}
            />
          )}
        {isPptBuilderV2 &&
          showSplitter?.resourceParams?.mode !== "iterate" &&
          showSplitter?.resourceParams?.mode !== "resume" &&
          typeof showSplitter?.resourceParams?.text_body === "string" &&
          showSplitter.resourceParams.text_body.length > 0 && (
            <AgentPptOpusPanel
              key={`v2-opus-gen-${(showSplitter.resourceParams.topic_label || "deck").slice(0, 32)}`}
              mode="generate"
              textBody={showSplitter.resourceParams.text_body}
              topicLabel={showSplitter.resourceParams.topic_label || "deck"}
              slideCount={showSplitter.resourceParams.slide_count ?? null}
              templateId={showSplitter.resourceParams.template_id ?? null}
              source={showSplitter.resourceParams.source ?? null}
              chatSessionId={
                chatSessionIdOverride ??
                showSplitter.resourceParams.chat_session_id ??
                null
              }
              projectName={showSplitter.resourceParams.project_name ?? null}
            />
          )}
        {isHtml && (
          <iframe
            title="html"
            srcDoc={showSplitter?.resourceParams?.data || ""}
            className="w-full h-full"
          />
        )}
        {isProactiveProposalLoading && <ProactiveProposalLoadingPanel />}
        {isProactiveProposalUnavailable && (
          <ProactiveProposalUnavailablePanel
            reason={showSplitter?.resourceParams?.reason}
          />
        )}
        {isDocumentArtifact &&
          typeof showSplitter?.resourceParams?.s3_path === "string" &&
          showSplitter.resourceParams.s3_path.length > 0 && (
            <AgentDocumentArtifact
              key={showSplitter.resourceParams.s3_path}
              s3Path={showSplitter.resourceParams.s3_path}
              format={showSplitter.resourceParams.format}
              title={showSplitter.resourceParams.title || ""}
            />
          )}
        {/* content for a fresh generation, session_id when opened from history */}
        {isHtmlPage &&
          (showSplitter?.resourceParams?.content ||
            showSplitter?.resourceParams?.session_id) && (
            <AgentHtmlPage
              // remount per edit so a second change re-applies (as PptBuilderV2 does)
              key={`${showSplitter.resourceParams.session_id || "new"}-${
                showSplitter.resourceParams.change || ""
              }`}
              content={showSplitter.resourceParams.content || ""}
              title={showSplitter.resourceParams.title || ""}
              resumeSessionId={showSplitter.resourceParams.session_id}
              change={showSplitter.resourceParams.change}
              docSessionId={showSplitter.resourceParams.doc_session_id}
              chatId={chatId}
              onClose={() => setShowSplitter(null)}
            />
          )}
        <ModalComponents />
      </Suspense>
    </div>
  )
}

function ProactiveProposalLoadingPanel() {
  const { t } = useTranslation()
  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-1">
      {/* min-h + border match the left proactive header (px-4 py-2 +
          h-9 selector ≈ 52px, border-b-[0.5px]) so the two header divider
          lines line up across the split. */}
      <div className="flex items-center gap-2 px-4 min-h-[52px] border-b-[0.5px] border-light-border dark:border-border">
        <span className="text-sm font-semibold text-dark-2 dark:text-dark-6">
          {t("rfp_os.label_powerpoint")}
        </span>
      </div>
      {/* Mirrors the PPT builder's slide-loading state (spinner +
          "Fetching slides…" + progress) on the white panel background
          instead of the opus panel's dark one. */}
      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
        <div className="w-10 h-10 border-[3px] border-primary border-t-transparent rounded-full animate-spin" />
        <div className="text-sm font-medium text-dark-2 dark:text-dark-6">
          {t("rfp_os.label_fetching_slides")}
        </div>
        <div className="w-56 h-1.5 rounded-full bg-gray-200 dark:bg-dark-2 overflow-hidden">
          <div className="h-full w-1/3 rounded-full bg-primary animate-pulse" />
        </div>
      </div>
    </div>
  )
}

// Shown when a session has no turns and none are coming. Mirrors the loading
// panel's chrome so the header divider still lines up with the left pane.
function ProactiveProposalUnavailablePanel({ reason }: { reason?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // "Couldn't fetch it" and "never started" are different claims — only say
  // the second when the read succeeded and came back empty.
  const failedToLoad = reason === "load_failed"
  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-1">
      <div className="flex items-center gap-2 px-4 min-h-[52px] border-b-[0.5px] border-light-border dark:border-border">
        <span className="text-sm font-semibold text-dark-2 dark:text-dark-6">
          {t("rfp_os.label_powerpoint")}
        </span>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="text-sm font-medium text-dark-2 dark:text-dark-6">
          {t(
            failedToLoad
              ? "rfp_os.label_proposal_load_failed"
              : "rfp_os.label_proposal_did_not_start"
          )}
        </div>
        <div className="text-xs text-dark-4 dark:text-dark-6 max-w-sm">
          {t(
            failedToLoad
              ? "rfp_os.label_proposal_load_failed_desc"
              : "rfp_os.label_proposal_did_not_start_desc"
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-8 px-3 rounded text-xs border border-light-border dark:border-border text-dark-2 dark:text-dark-6"
          >
            {t("rfp_os.label_retry")}
          </button>
          <button
            type="button"
            onClick={() => navigate("/rfp-os/rfp-rfi?tab=proactive")}
            className="h-8 px-3 rounded text-xs bg-primary text-white"
          >
            {t("rfp_os.label_start_new_proposal")}
          </button>
        </div>
      </div>
    </div>
  )
}

const ModalComponents = () => {
  const showMessageTemplateModal = useChampionsTrackingStore(
    (store) => store.showMessageTemplateModal
  )
  return <>{showMessageTemplateModal && <MessageTemplateModal />}</>
}
